import React, { useEffect, useMemo, useRef, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Stack,
  Switch,
  TableSortLabel,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CommonTable, { ReadOnlyCell } from "../../../utils/CommonTable";
import GroupedSelect from "../../../utils/GroupedSelect";
import { SearchableDropdown } from "../../../utils/Form/Dropdown";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { TextFieldForName } from "../../../utils/TextField/TextField";
import TaxField from "../../../utils/TextField/TaxField";
import CustomDatePicker from "../../../utils/DatePicker/CustomDatePicker";
import {
  formatAmount,
  formatDateForAPI,
} from "../../../../utils/hooks/dataHandler";
import {
  getFifoShareUsage,
  getTrackerWorkingText,
} from "./sharedBasedPaymentCalculation";
import TrackerMiniMetrics from "./TrackerMiniMetrics";
import TrackerManualEntryState from "./TrackerManualEntryState";
import TrackerToggle from "./TrackerToggle";
import {
  arrangementOptions,
  costMethodOptions,
  createClaimLine,
  createInitialCostPool,
  createTrackerTransaction,
  normalizeTrackerShares,
  renumberTrackerTransactions,
  removeTrackerTransaction,
  transactionTypeOptions,
} from "./sharedBasedPaymentData";

// Schedule-owned data keeps tracker edits and deduction results in sync across tabs.
const SharesCostTracker = ({
  data,
  onChange,
  calculationResults,
  currency,
  canEdit = true,
  onOpenDeduction,
  trackerLoading = false,
  onTrackerToggle,
}) => {
  const pool = data.costPools[0] || createInitialCostPool();
  const [dateSortDirection, setDateSortDirection] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const pendingFocusTransactionIdRef = useRef(null);
  const transactionDateRefs = useRef(new Map());
  const trackerResult = calculationResults?.trackerResults?.[0]?.summary;
  const selectedTransactions = data.transactions;
  const displayedTransactions = useMemo(() => {
    if (!dateSortDirection) return selectedTransactions;

    return [...selectedTransactions].sort((a, b) => {
      // Incomplete rows stay at the end in either direction.
      if (!a.date || !b.date) {
        if (!a.date && !b.date) return 0;
        return a.date ? -1 : 1;
      }
      const comparison = String(a.date).localeCompare(String(b.date));
      return dateSortDirection === "asc" ? comparison : -comparison;
    });
  }, [dateSortDirection, selectedTransactions]);
  const showFifoShares = pool.method === "fifo" && Boolean(data.showFifoShares);
  const fifoShareUsage = useMemo(
    () =>
      showFifoShares
        ? getFifoShareUsage(displayedTransactions, trackerResult)
        : {
            outflows: [],
            usedByOutflow: {},
            usedBySourceAndOutflow: {},
            closingSharesBySource: {},
          },
    [showFifoShares, displayedTransactions, trackerResult]
  );
  const unlinkedTransferCount = selectedTransactions.filter(
    (row) => row.type === "eebrTransfer" && !row.linkedClaimLineId
  ).length;
  const trackerIssues = useMemo(
    () =>
      [
        trackerResult?.issue,
        unlinkedTransferCount
          ? `${unlinkedTransferCount} EEBR ${
              unlinkedTransferCount === 1 ? "transfer needs" : "transfers need"
            } a linked deduction line.`
          : "",
      ].filter(Boolean),
    [trackerResult?.issue, unlinkedTransferCount]
  );

  useEffect(() => {
    const transactionId = pendingFocusTransactionIdRef.current;
    if (!transactionId) return undefined;

    const frame = window.requestAnimationFrame(() => {
      transactionDateRefs.current.get(transactionId)?.focus();
      pendingFocusTransactionIdRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [data.transactions]);
  const linkedLineOptions = useMemo(
    () =>
      data.claimLines.map((line) => {
        const arrangement =
          arrangementOptions.find((option) => option.value === line.arrangement)
            ?.label || "Arrangement not specified";
        const description =
          line.description?.trim() || line.schemeOrPoolLabel?.trim();
        const dates = [
          ["Exercise / vesting", line.exerciseOrVestingDate],
          ["Recharge / liability", line.rechargeOrLiabilityDate],
        ].flatMap(([label, value]) => {
          const date = value ? parseISO(value) : null;
          return isValid(date)
            ? [`${label}: ${format(date, "dd-MM-yyyy")}`]
            : [];
        });

        return {
          value: line.claimLineId,
          label: description || arrangement,
          reference: line.lineId,
          details: [description ? arrangement : null, ...dates]
            .filter(Boolean)
            .join(" · "),
          searchText: [
            line.schemeOrPoolLabel,
            line.exerciseOrVestingDate,
            line.rechargeOrLiabilityDate,
          ]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [data.claimLines]
  );

  const createAndLinkClaimLine = (transactionId) => {
    if (!canEdit) return;
    onChange((current) => {
      const newLine = createClaimLine(current);
      return {
        ...current,
        claimLines: [...current.claimLines, newLine],
        transactions: current.transactions.map((row) =>
          row.transactionId === transactionId
            ? { ...row, linkedClaimLineId: newLine.claimLineId }
            : row
        ),
      };
    });
  };

  const updatePool = (field, value) => {
    if (!canEdit) return;
    onChange((current) => ({
      ...current,
      costPools: [{ ...current.costPools[0], [field]: value }],
    }));
  };

  const updateTransaction = (transactionId, field, value) => {
    if (!canEdit) return;
    onChange((current) => ({
      ...current,
      transactions: current.transactions.map((row) =>
        row.transactionId === transactionId
          ? {
              ...row,
              [field]:
                field === "shares"
                  ? normalizeTrackerShares(value, row.type)
                  : value,
              ...(field === "type"
                ? { shares: normalizeTrackerShares(row.shares, value) }
                : {}),
              ...(field === "type" && value !== "eebrTransfer"
                ? { linkedClaimLineId: "" }
                : {}),
              ...(field === "type" && value !== "acquisition"
                ? { shareCost: "", directAcquisitionCharges: "" }
                : {}),
            }
          : row
      ),
    }));
  };

  const addTransaction = (type) => {
    if (!canEdit) return;
    onChange((current) => {
      const newTransaction = createTrackerTransaction(
        current,
        type,
        current.costPools[0]?.poolId
      );
      pendingFocusTransactionIdRef.current = newTransaction.transactionId;
      return {
        ...current,
        transactions: [...current.transactions, newTransaction],
      };
    });
  };

  const numberColumn = (
    field,
    label,
    decimals = 0,
    acquisitionOnly = false
  ) => ({
    key: field,
    label,
    width: "150px",
    headerAlign: "right",
    colAlign: "right",
    render: (row) =>
      acquisitionOnly && row.type !== "acquisition" ? (
        <ReadOnlyCell value='—' width='100%' textAlign='right' />
      ) : (
        <TaxField
          value={row[field] ?? ""}
          onChange={(value) =>
            updateTransaction(row.transactionId, field, value)
          }
          decimalScale={decimals}
          allowNegative={
            field === "shares" &&
            (row.type === "eebrTransfer" || row.type === "otherOutflow")
          }
          forceNegative={
            field === "shares" &&
            (row.type === "eebrTransfer" || row.type === "otherOutflow")
          }
          preserveEmpty
          canEdit={canEdit}
          fullWidth
          inputProps={{ "aria-label": `${label} ${row.transactionId}` }}
        />
      ),
  });

  const columns = [
    {
      key: "serialNo",
      label: "Txn no.",
      width: "120px",
      stickyLeft: 0,
      stickyDivider: true,
      render: (row) => (
        <ReadOnlyCell value={row.serialNo || row.transactionId} width='100%' />
      ),
    },
    {
      key: "date",
      label: (
        <TableSortLabel
          active={Boolean(dateSortDirection)}
          direction={dateSortDirection || "asc"}
          onClick={() =>
            setDateSortDirection((current) =>
              current === "asc" ? "desc" : "asc"
            )
          }
          aria-label={`Sort Date ${dateSortDirection === "asc" ? "descending" : "ascending"}`}
        >
          Date
        </TableSortLabel>
      ),
      width: "220px",
      render: (row) => {
        const selected = row.date ? parseISO(row.date) : null;
        return (
          <Box sx={{ "& .react-datepicker-popper": { zIndex: 3 } }}>
            <CustomDatePicker
              inputRef={(node) => {
                if (node) {
                  transactionDateRefs.current.set(row.transactionId, node);
                } else {
                  transactionDateRefs.current.delete(row.transactionId);
                }
              }}
              selected={isValid(selected) ? selected : null}
              onChange={(date) =>
                updateTransaction(
                  row.transactionId,
                  "date",
                  formatDateForAPI(date)
                )
              }
              disabled={!canEdit}
              ariaLabel={`Date ${row.transactionId}`}
              placeholder='DD-MM-YYYY'
              popperProps={{ strategy: "fixed" }}
              fullWidth
            />
          </Box>
        );
      },
    },
    {
      key: "type",
      label: "Type",
      width: "185px",
      render: (row) => (
        <Stack spacing={1}>
          <GroupedSelect
            value={row.type}
            options={transactionTypeOptions}
            onChange={(event) =>
              updateTransaction(row.transactionId, "type", event.target.value)
            }
            canEdit={canEdit}
          />
          {row.type === "otherOutflow" && (
            <TextField
              value={row.outflowDescription || ""}
              onChange={(event) =>
                updateTransaction(
                  row.transactionId,
                  "outflowDescription",
                  event.target.value
                )
              }
              label='Outflow description'
              placeholder='e.g. Shares sold'
              size='small'
              multiline
              minRows={1}
              maxRows={3}
              fullWidth
              InputProps={{ readOnly: !canEdit }}
              inputProps={{
                "aria-label": `Outflow description for ${row.serialNo || row.transactionId}`,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "background.paper",
                },
              }}
            />
          )}
        </Stack>
      ),
    },
    numberColumn("shares", "Shares"),
    ...(showFifoShares
      ? [
          ...fifoShareUsage.outflows.map((outflow) => {
            const date = outflow.date ? parseISO(outflow.date) : null;
            return {
              key: `fifoUsed-${outflow.transactionId}`,
              width: "150px",
              headerAlign: "right",
              colAlign: "right",
              headerGroup: { key: "fifoSharesUsed", label: "Shares used" },
              label: (
                <Stack spacing={0.25} alignItems='flex-end'>
                  <Box component='span' sx={{ textTransform: "none" }}>
                    {isValid(date) ? format(date, "dd-MMM-yy") : "No date"}
                  </Box>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 500, textTransform: "none" }}
                  >
                    {outflow.serialNo || outflow.transactionId}
                  </Typography>
                </Stack>
              ),
              render: (row) => {
                const used =
                  fifoShareUsage.usedBySourceAndOutflow[row.transactionId]?.[
                    outflow.transactionId
                  ] || 0;
                const value =
                  row.transactionId === outflow.transactionId
                    ? fifoShareUsage.usedByOutflow[outflow.transactionId]
                    : (row.type === "acquisition" || row.type === "opening") &&
                        used
                      ? `(${formatAmount(used)})`
                      : null;
                return (
                  <ReadOnlyCell
                    value={
                      value
                        ? typeof value === "number"
                          ? formatAmount(value)
                          : value
                        : "—"
                    }
                    width='100%'
                    textAlign='right'
                  />
                );
              },
            };
          }),
          {
            key: "fifoClosingShares",
            label: "No. of shares c/f",
            width: "180px",
            headerAlign: "right",
            colAlign: "right",
            render: (row) => {
              const remaining =
                fifoShareUsage.closingSharesBySource[row.transactionId] || 0;
              return (
                <ReadOnlyCell
                  value={
                    (row.type === "acquisition" || row.type === "opening") &&
                    remaining > 0
                      ? formatAmount(remaining)
                      : "—"
                  }
                  width='100%'
                  textAlign='right'
                />
              );
            },
          },
        ]
      : []),
    numberColumn("shareCost", "Share cost", 0, true),
    numberColumn("directAcquisitionCharges", "Charges", 0, true),
    {
      key: "linkedClaimLineId",
      label: "Linked line",
      width: "360px",
      render: (row) =>
        row.type === "eebrTransfer" ? (
          <SearchableDropdown
            value={row.linkedClaimLineId || ""}
            options={linkedLineOptions}
            onChange={(value) =>
              updateTransaction(row.transactionId, "linkedClaimLineId", value)
            }
            canEdit={canEdit}
            ariaLabel='Linked deduction line'
            placeholder='Search description, arrangement, date or ID'
            noOptionsText={
              data.claimLines.length
                ? "No matching deduction lines"
                : "Add a qualifying deduction line first"
            }
            actionLabel='Create deduction line'
            onAction={() => createAndLinkClaimLine(row.transactionId)}
          />
        ) : (
          <ReadOnlyCell value='—' width='100%' />
        ),
    },
    {
      key: "columnI",
      label: "Column I",
      width: "150px",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => {
        const working = trackerResult?.transactionWorkings?.[row.transactionId];
        const isLinked = row.type === "eebrTransfer" && row.linkedClaimLineId;
        return (
          <ReadOnlyCell
            value={
              isLinked && !trackerResult?.issue && working?.releasedCost != null
                ? formatAmount(working.releasedCost)
                : "—"
            }
            width='100%'
            textAlign='right'
          />
        );
      },
    },
    {
      key: "methodWorking",
      label: "Method working",
      width: "320px",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <Typography
          variant='body2'
          color={trackerResult?.issue ? "warning.main" : "text.secondary"}
          sx={{
            whiteSpace: "pre-line",
            overflowWrap: "anywhere",
            lineHeight: 1.6,
          }}
        >
          {trackerResult?.issue ||
            getTrackerWorkingText(
              trackerResult?.transactionWorkings?.[row.transactionId],
              pool.method,
              data.transactions
            )}
        </Typography>
      ),
    },
    {
      key: "reference",
      label: "Reference",
      responsiveWidth: "220px",
      render: (row) => (
        <TextFieldForName
          key={row.transactionId}
          value={row.reference || ""}
          onChange={(value) =>
            updateTransaction(row.transactionId, "reference", value)
          }
          inputProps={{ "aria-label": `Reference ${row.transactionId}` }}
          canEdit={canEdit}
        />
      ),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            label: "Actions",
            render: (row) => (
              <IconButtonWithTooltip
                icon={DeleteOutlineIcon}
                tooltipTitle={`Delete ${row.transactionId}`}
                color='error'
                size='small'
                onClick={() =>
                  onChange((current) =>
                    removeTrackerTransaction(
                      current,
                      row.transactionId,
                      displayedTransactions.map((item) => item.transactionId)
                    )
                  )
                }
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={2.5} sx={{ minWidth: 0, px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box
        component='section'
        aria-labelledby='share-pool-title'
        sx={{ minWidth: 0 }}
      >
        {data.useCostTracker ? (
          <>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              useFlexGap
              flexWrap='wrap'
              gap={1.5}
              sx={{ px: { xs: 1, sm: 2 }, pb: 1.5 }}
            >
              <Typography
                id='share-pool-title'
                variant='subtitle1'
                fontWeight={600}
              >
                Share pool
              </Typography>
              <Tooltip
                title='Automatically calculate share costs and populate Column I from linked EEBR transfers. Turn off for manual entry.'
                arrow
                describeChild
              >
                <Box
                  component='span'
                  sx={{ display: "inline-flex", borderRadius: 2 }}
                >
                  <TrackerToggle
                    checked={data.useCostTracker}
                    disabled={!canEdit}
                    loading={trackerLoading}
                    onChange={onTrackerToggle}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  alignItems: "end",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {[
                  ["poolName", "Pool name"],
                  ["issuer", "Issuer"],
                  ["shareClass", "Share class"],
                ].map(([field, label]) => (
                  <Box key={`${pool.poolId}-${field}`} sx={{ minWidth: 0 }}>
                    <Typography
                      component='label'
                      htmlFor={`tracker-pool-${field}`}
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    <TextField
                      key={`${pool.poolId}-${field}`}
                      id={`tracker-pool-${field}`}
                      inputProps={{ "aria-label": label }}
                      value={pool[field] || ""}
                      onChange={(event) =>
                        updatePool(field, event.target.value)
                      }
                      InputProps={{ readOnly: !canEdit }}
                      size='small'
                      fullWidth
                    />
                  </Box>
                ))}
                <Box role='group' aria-label='Cost method' sx={{ minWidth: 0 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Cost method
                  </Typography>
                  <GroupedSelect
                    value={pool.method || "fifo"}
                    options={costMethodOptions}
                    onChange={(event) =>
                      updatePool("method", event.target.value)
                    }
                    canEdit={canEdit}
                    placeholder='Select cost method'
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
              }}
            >
              <TrackerMiniMetrics
                results={calculationResults?.trackerResults}
                currency={currency}
                closingOnly
                sx={{ m: 0, pt: 0, borderTop: 0 }}
              />
            </Box>
          </>
        ) : (
          <TrackerManualEntryState
            onOpenDeduction={onOpenDeduction}
            canEdit={canEdit}
            loading={trackerLoading}
            onEnableTracker={() => onTrackerToggle(true)}
            transactionCount={data.transactions.length}
          />
        )}
      </Box>

      {data.useCostTracker && (
        <>
          <Divider />
          {trackerIssues.length > 0 && (
            <Alert
              severity='error'
              variant='standard'
              sx={(theme) => ({
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                border: "1px solid",
                borderColor: alpha(theme.palette.error.main, 0.4),
                borderLeft: "6px solid",
                borderLeftColor: "error.main",
                borderRadius: 2,
                bgcolor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === "dark" ? 0.16 : 0.1
                ),
                color: "text.primary",
                "& .MuiAlert-icon": {
                  color: "error.main",
                  fontSize: 28,
                  opacity: 1,
                  mr: { xs: 1, sm: 1.5 },
                },
                "& .MuiAlert-message": { minWidth: 0, width: "100%" },
              })}
            >
              <AlertTitle
                sx={{
                  mb: 1,
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.error.light
                      : theme.palette.error.dark,
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  flexWrap='wrap'
                  gap={1}
                >
                  <span>Tracker needs attention</span>
                  <Chip
                    size='small'
                    color='error'
                    label={`${trackerIssues.length} ${
                      trackerIssues.length === 1 ? "issue" : "issues"
                    }`}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </AlertTitle>
              <Stack component='ul' spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
                {trackerIssues.map((issue) => (
                  <Typography
                    component='li'
                    variant='body2'
                    key={issue}
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.6,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {issue}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
          <Box
            component='section'
            aria-labelledby='tracker-transactions-title'
            sx={{
              minWidth: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Stack
              spacing={1.25}
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                useFlexGap
                flexWrap='wrap'
                gap={1.5}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    id='tracker-transactions-title'
                    variant='subtitle1'
                    fontWeight={600}
                  >
                    Transactions
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {selectedTransactions.length}{" "}
                    {selectedTransactions.length === 1 ? "entry" : "entries"}
                  </Typography>
                </Stack>
                {canEdit && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    useFlexGap
                    flexWrap='wrap'
                    gap={1}
                    role='group'
                    aria-label='Transaction actions'
                    sx={{ ml: "auto" }}
                  >
                    <Tooltip title='Update transaction numbers to match the current table order, including date sorting. Method working references update automatically.'>
                      <span>
                        <Button
                          size='small'
                          startIcon={<FormatListNumberedIcon />}
                          disabled={!displayedTransactions.length}
                          color='inherit'
                          onClick={() =>
                            onChange((current) =>
                              renumberTrackerTransactions(
                                current,
                                displayedTransactions.map(
                                  (row) => row.transactionId
                                )
                              )
                            )
                          }
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            color: "text.secondary",
                          }}
                        >
                          Renumber
                        </Button>
                      </span>
                    </Tooltip>
                    <ButtonGroup
                      size='small'
                      variant='contained'
                      disableElevation
                      aria-label='Add transaction'
                      sx={{
                        "& .MuiButton-root": {
                          textTransform: "none",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <Tooltip title='Add an acquisition. Use the arrow to choose a transfer or other outflow.'>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addTransaction("acquisition")}
                        >
                          <Box
                            component='span'
                            sx={{ display: { xs: "inline", sm: "none" } }}
                          >
                            Add
                          </Box>
                          <Box
                            component='span'
                            sx={{ display: { xs: "none", sm: "inline" } }}
                          >
                            Add transaction
                          </Box>
                        </Button>
                      </Tooltip>
                      <Tooltip title='Choose transaction type'>
                        <Button
                          aria-label='Choose transaction type'
                          aria-haspopup='menu'
                          aria-controls={
                            addMenuAnchor
                              ? "tracker-add-transaction-menu"
                              : undefined
                          }
                          aria-expanded={Boolean(addMenuAnchor)}
                          onClick={(event) =>
                            setAddMenuAnchor(event.currentTarget)
                          }
                          sx={{ px: 0.5, minWidth: "32px !important" }}
                        >
                          <ArrowDropDownIcon fontSize='small' />
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                    <Menu
                      id='tracker-add-transaction-menu'
                      anchorEl={addMenuAnchor}
                      open={Boolean(addMenuAnchor)}
                      onClose={() => setAddMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {transactionTypeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => {
                            setAddMenuAnchor(null);
                            addTransaction(option.value);
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Stack>
                )}
              </Stack>
              {pool.method === "fifo" && (
                <Stack
                  direction='row'
                  alignItems='center'
                  useFlexGap
                  flexWrap='wrap'
                  gap={1.5}
                  role='group'
                  aria-label='Transaction display options'
                  sx={{
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Table view
                  </Typography>
                  <Tooltip
                    title='Show which acquisition supplied each outflow and how many shares remain in each acquisition.'
                    arrow
                    describeChild
                  >
                    <Box
                      component='span'
                      tabIndex={0}
                      sx={{ display: "inline-flex", minWidth: 0 }}
                    >
                      <FormControlLabel
                        label='Show FIFO calculations'
                        sx={{
                          m: 0,
                          gap: 0.5,
                          color: showFifoShares
                            ? "success.main"
                            : "text.secondary",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8125rem",
                            fontWeight: showFifoShares ? 500 : 400,
                          },
                        }}
                        control={
                          <Switch
                            size='small'
                            color='success'
                            checked={showFifoShares}
                            disabled={!canEdit}
                            onChange={(_, checked) =>
                              onChange((current) => ({
                                ...current,
                                showFifoShares: checked,
                              }))
                            }
                          />
                        }
                      />
                    </Box>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <Box
              sx={{
                height: "clamp(320px, 55vh, 640px)",
                minWidth: 0,
                minHeight: 320,
                overflow: "hidden",
                "& .MuiTableContainer-root": {
                  height: "100%",
                  borderRadius: 0,
                  overflow: "auto",
                },
                // Keep all header rows on one opaque sticky surface.
                "& .MuiTableHead-root": {
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  bgcolor: "background.paper",
                },
                "& .MuiTableCell-head": {
                  top: "auto",
                },
              }}
            >
              <CommonTable
                rows={displayedTransactions}
                columns={columns}
                noRowsHint='No transactions yet. Add an acquisition or transfer to get started.'
              />
            </Box>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default SharesCostTracker;
